/**
 * Floating mini-timer (picture-in-picture) + "time's up" alert.
 * Any timer calls setFloatingTimer({...}) while running and clearFloatingTimer()
 * when stopped. A canvas (gold ring + time) is streamed into a hidden <video>;
 * when the user leaves the app we pop that video out as a PiP window.
 * Browsers may require a tap to open PiP; showFloatButton reflects that fallback.
 */
const SIZE = 320;
let state = null; // { label, mode: 'countdown'|'stopwatch', endAt, totalMs, startAt, baseMs, paused, pausedMs }
let controls = {};
// Each timer owns an entry; the most recent one is shown, and clearing it falls back to the previous one.
const owners = new Map(); let current = null;
let canvas; let video; let ticker; let alertTimer; let listeners = new Set();

// Worker-driven ticks keep the PiP canvas updating when the page is hidden (main-thread timers get throttled).
const startTicker = () => {
  if (ticker) return;
  try {
    const src = 'setInterval(() => postMessage(0), 100);';
    ticker = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
    ticker.onmessage = draw;
  } catch { ticker = { terminate: clearInterval.bind(null, setInterval(draw, 250)) }; }
};
const stopTicker = () => { ticker?.terminate(); ticker = null; };
const inPip = () => !!video && document.pictureInPictureElement === video;

const supported = () => typeof document !== 'undefined' && 'pictureInPictureEnabled' in document && document.pictureInPictureEnabled
  && !!HTMLCanvasElement.prototype.captureStream;

// Countdowns round up and stopwatches round down, matching the in-app clocks so both flip on the same second.
const fmt = (ms, up) => {
  const t = Math.max(0, up ? Math.ceil(ms / 1000) : Math.floor(ms / 1000));
  const h = Math.floor(t / 3600); const m = Math.floor((t % 3600) / 60); const s = t % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${p(m)}:${p(s)}` : `${m}:${p(s)}`;
};

const remaining = () => {
  if (!state) return 0;
  if (state.mode === 'stopwatch') return state.paused ? state.pausedMs : state.baseMs + (Date.now() - state.startAt);
  return state.paused ? state.pausedMs : Math.max(0, state.endAt - Date.now());
};

function draw() {
  if (!canvas || !state) return;
  const ctx = canvas.getContext('2d');
  const ms = remaining();
  ctx.fillStyle = '#000'; ctx.fillRect(0, 0, SIZE, SIZE);
  const cx = SIZE / 2; const r = 128; const ticks = 60;
  const frac = state.mode === 'countdown' && state.totalMs ? ms / state.totalMs : 1;
  for (let i = 0; i < ticks; i += 1) {
    const a = (i / ticks) * Math.PI * 2 - Math.PI / 2;
    ctx.strokeStyle = i / ticks < frac ? '#FFD700' : 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 6; ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * (r - 14), cx + Math.sin(a) * (r - 14));
    ctx.lineTo(cx + Math.cos(a) * r, cx + Math.sin(a) * r);
    ctx.stroke();
  }
  ctx.textAlign = 'center'; ctx.fillStyle = '#fff';
  ctx.font = 'bold 64px Manrope, Arial, sans-serif';
  ctx.fillText(state.mode === 'countdown' && ms === 0 ? "Time's up" : fmt(ms, state.mode === 'countdown'), cx, cx + 20);
  ctx.font = '600 20px Manrope, Arial, sans-serif'; ctx.fillStyle = state.paused ? 'rgba(255,255,255,0.45)' : '#FFD700';
  ctx.fillText((state.paused ? 'Paused' : state.label || 'Timer').toUpperCase().slice(0, 22), cx, cx - 52);
}

function ensureVideo() {
  if (video || !supported()) return;
  canvas = document.createElement('canvas'); canvas.width = SIZE; canvas.height = SIZE;
  draw();
  video = document.createElement('video');
  Object.assign(video, { muted: true, playsInline: true, autoplay: true });
  video.setAttribute('autopictureinpicture', '');
  video.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;bottom:0;left:0;';
  video.srcObject = canvas.captureStream(10);
  document.body.appendChild(video);
  video.play().catch(() => {});
  const onPip = () => emit();
  video.addEventListener('enterpictureinpicture', onPip);
  video.addEventListener('leavepictureinpicture', onPip);
  try {
    navigator.mediaSession?.setActionHandler?.('enterpictureinpicture', () => openFloating());
  } catch { /* not supported */ }
}

async function notifyDone(label) {
  try { navigator.vibrate?.([300, 100, 300, 100, 500]); } catch { /* noop */ }
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker?.getRegistration?.();
    const opts = { body: `${label || 'Your timer'} is done.`, icon: '/logo192.png', tag: 'timer-done', renotify: true, vibrate: [300, 100, 300] };
    if (reg) reg.showNotification("Time's up!", opts); else new Notification("Time's up!", opts);
  } catch { /* noop */ }
}

function scheduleAlert() {
  clearTimeout(alertTimer);
  if (!state || state.mode !== 'countdown' || state.paused || state.alert === false) return;
  const label = state.label;
  alertTimer = setTimeout(() => notifyDone(label), Math.max(0, state.endAt - Date.now()));
}

// Play/pause (and "next" for skip) buttons inside the floating window via Media Session.
function syncSession() {
  const ms = typeof navigator !== 'undefined' && navigator.mediaSession;
  if (!ms) return;
  const set = (action, fn) => { try { ms.setActionHandler(action, fn || null); } catch { /* unsupported */ } };
  const keepPlaying = () => video?.play().catch(() => {});
  set('play', controls.onResume && (() => { controls.onResume(); keepPlaying(); }));
  set('pause', controls.onPause && (() => { controls.onPause(); keepPlaying(); }));
  set('nexttrack', controls.onSkip && (() => controls.onSkip()));
  set('previoustrack', controls.onAddTime && (() => controls.onAddTime()));
  try {
    ms.playbackState = state ? (state.paused ? 'paused' : 'playing') : 'none';
    ms.metadata = state && window.MediaMetadata ? new window.MediaMetadata({ title: state.label || 'Timer', artist: 'Ascend & Surpass' }) : null;
  } catch { /* unsupported */ }
}

export function setFloatingControls(next, owner = 'default') {
  owners.set(owner, { ...owners.get(owner), controls: next || {} });
  if (owner === current) { controls = next || {}; syncSession(); }
}

const emit = () => listeners.forEach((fn) => fn(!!state && !inPip()));

export function setFloatingTimer(next, owner = 'default') {
  const entry = owners.get(owner) || {};
  owners.delete(owner); owners.set(owner, { ...entry, state: next });
  current = owner; controls = entry.controls || {};
  const fresh = !state;
  const resumed = state?.paused && !next.paused;
  state = { ...next };
  ensureVideo();
  startTicker();
  draw();
  scheduleAlert();
  syncSession();
  if (fresh) emit();
  // Runs right after the user's Start tap, so the browser allows opening the floating window now.
  if ((fresh || resumed) && !next.paused) openFloating();
}

export function clearFloatingTimer(owner = 'default') {
  owners.delete(owner);
  if (owner !== current) return;
  const prev = [...owners].reverse().find(([, e]) => e.state);
  if (prev) {
    [current] = prev; state = { ...prev[1].state }; controls = prev[1].controls || {};
    draw(); scheduleAlert(); syncSession();
    return;
  }
  current = null; state = null; controls = {};
  clearTimeout(alertTimer); stopTicker(); syncSession();
  if (inPip()) document.exitPictureInPicture().catch(() => {});
  emit();
}

export async function openFloating() {
  if (!video || !state) return false;
  try {
    if (video.readyState < 1) {
      await new Promise((res) => { video.addEventListener('loadedmetadata', res, { once: true }); setTimeout(res, 1500); });
    }
    if (video.paused) await video.play();
    if (!inPip()) await video.requestPictureInPicture();
    return true;
  } catch { emit(); return false; }
}

export const floatingSupported = supported;
export const onFloatingChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export function requestTimerAlerts() {
  try { if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission(); } catch { /* noop */ }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && state && !inPip()) openFloating();
    // Back in the app: hide the floating clock so it isn't shown twice.
    if (document.visibilityState === 'visible' && inPip()) document.exitPictureInPicture().catch(() => {});
  });
}
